<!--
  @secui/vue-data-table-2.0 "Dashboard > 장비 현황" 전용 Wrap 컴포넌트
  @since 2023-12-01
  @author jungmin87.kim
   - customType
      -> eventLevel: Level
-->

<template>
  <vue-table ref="dataTable"
             :row-key="rowKey"
             :columns="columns"
             :data="data"
             :height="height"
             :page-size="pageSize"
             :page-margin="pageMargin"
             :window-resize="windowResize"
             :resize-mode="resizeMode"
             :sortable="sortable"
             :virtual-scroll="virtualScroll"
             :is-popup="isPopup"
             @update:selected="(val) => {$emit('update:selected', val)}"
             :class="[ 'bs', 'bs-data-table', (slim ? 'bs-data-table-slim' : 'bs-data-table-basic'), 'analysis-panel-datatable' ]"
  >
    <template v-slot:button-event_level_name="props">
      <column-list-checkbox-filter ref="colCheckFilter"
                                   :vue-table-props="props"
                                   :opts="eventLevelFiltersOpts"
                                   @columnFilter="filter => setEventLevelFilters('event_level_name', filter)"
      />
    </template>
    <!-- Body -->
    <template #body="props">
      <vue-table-tr :props="props" @onRowDblClick="onRowDblClick">
        <vue-table-td
          v-for="col of props.cols"
          :key="col.field"
          :class="col.field === 'event_level_name' ? 'no-padding' : ''"
          :props="props"
        >
          <!-- Level -->
          <div v-if="getCustomType(col) === 'eventLevel'" style="height: 100%">
            <div :style="getLevelStyles(props.row.event_level_type)">{{getLevelName(props.row.event_level_type)}}</div>
          </div>
          <!-- tnlStatus (터널 상태) -->
          <div v-else-if="getCustomType(col) === 'tnlStatus'" style="height: 100%">
            <div :style="getTnlStatusStyles(props.row.sdwan_tnl_status)">{{getTnlStatusName(props.row.sdwan_tnl_status)}}</div>
          </div>
          <!-- Text -->
          <div v-else>
            <span class="marL5">{{ props.row[col.field] }}</span>
          </div>
        </vue-table-td>
      </vue-table-tr>
    </template>
  </vue-table>
</template>

<script>
import {createNamespacedHelpers} from 'vuex'
const widgetDetailHelper = createNamespacedHelpers('dashboard/widgetDetail')
const deviceStatusHelper = createNamespacedHelpers('dashboard/deviceStatus')

import { getColorLuminance } from '~/utils/charts'
import ColumnListCheckboxFilter from '../filter/ColumnListCheckboxFilter.vue'
export default {
  name: 'deviceStatusDataTable',
  components: {ColumnListCheckboxFilter},
  props: {
    tableName: {
      type: String,
      default: ''
    },
    rowKey: {
      type: String,
      default: ''
    },
    columns: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    height: {
      type: Number,
      default: 200
    },
    pageSize: {
      type: Number,
      default: 0
    },
    pageMargin: {
      type: Number,
      default: 0
    },
    windowResize: {
      type: Boolean,
      default: false
    },
    resizeMode: { // percent, overflow
      type: String,
      default: 'percent'
    },
    sortable: { // 정렬
      type: Boolean,
      default: false
    },
    virtualScroll: {
      type: Boolean,
      default: true
    },
    slim: {
      type: Boolean,
      default: true
    },
    isPopup: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      filters: {} // 필터[tableName: {[filters]}]
    }
  },
  computed: {
    ...widgetDetailHelper.mapState([
      'eventLevelInfo'
    ]),
    ...deviceStatusHelper.mapState([
      'eventLogFilter'
    ]),
    eventLevelFiltersOpts () {
      const filter = _.get(this.filters, 'event_level_name')
      return [
        {label: 'Info', value: 1, use: _.get(filter, '1', false)},
        {label: 'Low', value: 2, use: _.get(filter, '2', false)},
        {label: 'Medium', value: 3, use: _.get(filter, '3', false)},
        {label: 'High', value: 4, use: _.get(filter, '4', false)},
        {label: 'Critical', value: 5, use: _.get(filter, '5', false)}
      ]
    }
    // filteredDataList () {
    //   if (Object.keys(this.eventLevelfilters).length === 0) return _.cloneDeep(this.data)
    //   return _.cloneDeep(_.filter(this.data, o => {
    //     if (_.has(this.eventLevelfilters, [o.event_level_name])) {
    //       return _.get(this.eventLevelfilters, [o.event_level_name], false)
    //     }
    //     return false
    //   }))
    // }
  },
  mounted () {
    this.filters = this.getFilter() // store에 저장된 필터를 가져온다.
    this.injectFilter() // this.filters에 필터를 테이블에 상태를 적용한다.
  },
  methods: {
    ...deviceStatusHelper.mapMutations([
      'setEventLogDataFilter'
    ]),
    // template
    getTextTemplate (row, { field }) {
      return _.get(row, [field], '')
    },
    onRowDblClick ({row, evt}) {
      this.$emit('onRowDblClick', {row, evt})
    },
    openPage (pageIndex = 1) {
      this.$refs.dataTable.setPagination({ page: pageIndex })
    },
    getLevelName (eventLevelType) {
      const dict = {
        1: 'info',
        2: 'Low',
        3: 'Medium',
        4: 'High',
        5: 'Critical'
      }
      return _.get(dict, [eventLevelType], dict[4])
    },
    getLevelStyles (eventLevelType) {
      const defaultStyle = 'height: 100%;display: flex; align-items: center; justify-content: center;'
      const backgroundColor = this.getEventColor(eventLevelType)
      const textColor = getColorLuminance(backgroundColor)
      return `${defaultStyle}background:${backgroundColor};color:${textColor};`
    },
    getEventColor (eventLevelType) {
      let eventColor = ''
      try {
        eventColor = this.eventLevelInfo.find(info => info.event_level_type === eventLevelType).event_level_color || ''
      } catch (e) {
        eventColor = this.eventLevelInfo[0].event_level_color
      }
      return eventColor
    },
    getTnlStatusName (tnlStatus) {
      const dict = {
        1: 'On',
        2: 'Off',
        3: 'Expired',
        4: 'Unknown'
      }
      return _.get(dict, [tnlStatus], dict[4])
    },
    getTnlStatusStyles (tnlStatus) {
      const dict = {
        1: '#00B050', // 녹색
        2: '#FF0000', // 붉은색
        3: '#FF0000',
        4: '#FF0000'
      }
      const defaultStyle = 'height: 22px;display: flex; align-items: center; justify-content: center;border-radius: 5px; margin-top: 1px; margin-bottom: 1px;'
      const backgroundColor = _.get(dict, [tnlStatus], dict[4])
      const textColor = getColorLuminance(backgroundColor)
      return `${defaultStyle}background:${backgroundColor};color:${textColor};`
    },
    setEventLevelFilters (tableKey, filter) {
      _.set(this.filters, [tableKey], filter)
      this.saveFilter()
      this.$emit('columnFilter', {tableKey, filter})
    },
    getCustomType (column) {
      return _.get(column, ['custom', 'type'], '')
    },
    /** Public Function**/
    getFilterList () {
      return Object.keys(this.eventLevelFilters)
    },
    /** 필터 관리 (sessionStorage에서 상태 관리) **/
    saveFilter () {
      this.setEventLogDataFilter({tableKey: this.tableName, filters: this.filters})
    },
    getFilter () {
      return _.get(this.eventLogFilter, [this.tableName], {})
    },
    injectFilter () {
      _.forEach(this.filters, (filter, key) => {
        const idx = _.findIndex(this.columns, o => o.field === key)
        if (idx === -1) return
        const isFiltered = Object.key(filter).length > 0
        // 필터 색상 변경
        const afterIconClass = _.get(this.columns, [idx, 'headerType', 0, 'props', 'iconCustomClass'])
        const beforeIconClass = isFiltered ? afterIconClass.replace('-filter-b', '-filter-r') : afterIconClass.replace('-filter-r', '-filter-b')
        _.set(this.columns, [idx, 'headerType', 0, 'props', 'iconCustomClass'], beforeIconClass)
      })
    }
  }
}
</script>
